/**
 * Iter 85.1 / Phase E6 Sprint 1 — ESS My Activity (D41).
 *
 * Personal activity self-view for the authenticated user. Tabs:
 *   • Today
 *   • This Week
 *   • This Month
 *
 * Privacy-safe — operator NEVER sees passwords, keystrokes, screens,
 * camera, mic, URLs, clipboard, or coordinates. The privacy banner is
 * mandatory on every workforce surface (D41 UI requirement).
 */
import { useCallback, useEffect, useState } from "react";
import { Sparkle, Clock, House, Calendar } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import WorkforcePrivacyBanner from "../../components/workforce/WorkforcePrivacyBanner";

const CLASS_BADGE = {
    ACTIVE: "bg-emerald-100 text-emerald-800",
    UNDERUTILIZED: "bg-amber-100 text-amber-800",
    INACTIVE: "bg-rose-100 text-rose-800",
    OVERLOADED: "bg-orange-100 text-orange-800",
    UNCLASSIFIED: "bg-slate-100 text-slate-600",
};

const hrs = (m) => (m == null ? "—" : `${(m / 60).toFixed(1)}h`);

export default function EssMyActivity({ embedded = false }) {
    const [today, setToday] = useState(null);
    const [weekly, setWeekly] = useState([]);
    const [monthly, setMonthly] = useState([]);
    const [loading, setLoading] = useState(false);
    const [linkErr, setLinkErr] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [t, w, m] = await Promise.all([
                api.get("/workforce/me/today").catch(e => { throw e; }),
                api.get("/workforce/me/weekly"),
                api.get("/workforce/me/monthly"),
            ]);
            setToday(t.data);
            setWeekly(w.data?.items || []);
            setMonthly(m.data?.items || []);
            setLinkErr(false);
        } catch (err) {
            const msg = err?.response?.data?.detail || "";
            if (typeof msg === "string" && msg.includes("not linked to an employee")) {
                setLinkErr(true);
            } else {
                toast.error(formatApiError(err, "Failed to load activity"));
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    return (
        <div className={embedded ? "pt-4 space-y-4" : "p-6 space-y-4"} data-testid="ess-my-activity">
            {!embedded && <PageHeader title="My Activity" />}

            <WorkforcePrivacyBanner />

            {linkErr && (
                <div className="border border-amber-200 bg-amber-50/40 rounded p-4 text-sm text-amber-900"
                    data-testid="ess-activity-no-link">
                    Your user account is not linked to an employee record yet — activity tracking
                    starts as soon as HR finishes provisioning your employee profile. (Heartbeats
                    are still emitted but ignored until linkage is complete.)
                </div>
            )}

            <Tabs defaultValue="today">
                <TabsList data-testid="ess-activity-tabs">
                    <TabsTrigger value="today" data-testid="ess-tab-today">
                        <Clock size={14} className="mr-1" /> Today
                    </TabsTrigger>
                    <TabsTrigger value="week" data-testid="ess-tab-week">
                        <House size={14} className="mr-1" /> This Week
                    </TabsTrigger>
                    <TabsTrigger value="month" data-testid="ess-tab-month">
                        <Calendar size={14} className="mr-1" /> This Month
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="today" className="space-y-3 mt-3" data-testid="ess-today-tab">
                    {today && <TodayCard rollup={today} />}
                </TabsContent>

                <TabsContent value="week" data-testid="ess-week-tab" className="mt-3">
                    <RollupTable items={weekly} testId="ess-week-table" />
                </TabsContent>

                <TabsContent value="month" data-testid="ess-month-tab" className="mt-3">
                    <RollupTable items={monthly} testId="ess-month-table" />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function TodayCard({ rollup }) {
    return (
        <div className="space-y-3" data-testid="ess-today-card">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Logged-in" value={hrs(rollup.total_logged_in_minutes)} />
                <Stat label="Active" value={hrs(rollup.total_active_minutes)} tone="emerald" />
                <Stat label="Idle" value={hrs(rollup.total_idle_minutes)} tone="amber" />
                <Stat label="Break" value={hrs(rollup.total_break_minutes)} tone="slate" />
            </div>
            <div className="border rounded p-4 bg-slate-50/40">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase text-slate-500 tracking-wider">Activity</div>
                        <div className="text-3xl font-mono font-bold mt-1" data-testid="ess-today-activity-pct">
                            {rollup.activity_pct}%
                        </div>
                    </div>
                    <div>
                        <Badge className={CLASS_BADGE[rollup.classification] || ""}
                            data-testid="ess-today-classification">
                            {rollup.classification}
                        </Badge>
                    </div>
                </div>
                <div className="text-xs text-slate-600 mt-2" data-testid="ess-today-reason">
                    {rollup.classification_reason}
                </div>
                <div className="text-[10px] text-slate-400 mt-3 font-mono">
                    Login: {(rollup.login_time || "—").slice(0, 16).replace("T", " ")} ·
                    Logout: {(rollup.logout_time || "—").slice(0, 16).replace("T", " ")} ·
                    Last activity: {(rollup.last_activity_at || "—").slice(0, 16).replace("T", " ")} ·
                    Heartbeats: {rollup.n_heartbeats}
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, tone }) {
    const toneMap = {
        emerald: "bg-emerald-50 border-emerald-200",
        amber: "bg-amber-50 border-amber-200",
        rose: "bg-rose-50 border-rose-200",
        slate: "bg-slate-50 border-slate-200",
    };
    return (
        <div className={`border rounded p-3 ${toneMap[tone] || "bg-white"}`}>
            <div className="text-[10px] uppercase text-slate-500 tracking-wider">{label}</div>
            <div className="font-mono text-lg font-bold mt-1">{value}</div>
        </div>
    );
}

function RollupTable({ items, testId }) {
    return (
        <div className="border rounded">
            <table className="w-full text-xs" data-testid={testId}>
                <thead className="bg-slate-50 border-b uppercase text-slate-600 text-[10px]">
                    <tr>
                        <th className="text-left px-2 py-1">Date</th>
                        <th className="text-left px-2 py-1">Login</th>
                        <th className="text-left px-2 py-1">Logout</th>
                        <th className="text-right px-2 py-1">Logged-in</th>
                        <th className="text-right px-2 py-1">Active</th>
                        <th className="text-right px-2 py-1">Idle</th>
                        <th className="text-right px-2 py-1">Break</th>
                        <th className="text-right px-2 py-1">Activity %</th>
                        <th className="text-left px-2 py-1">Class</th>
                    </tr>
                </thead>
                <tbody>
                    {(items || []).map((it) => (
                        <tr key={it.date} className="border-b" data-testid={`ess-rollup-row-${it.date}`}>
                            <td className="px-2 py-1 font-mono">{it.date}</td>
                            <td className="px-2 py-1 font-mono text-[10px]">{(it.login_time || "—").slice(11, 16)}</td>
                            <td className="px-2 py-1 font-mono text-[10px]">{(it.logout_time || "—").slice(11, 16)}</td>
                            <td className="px-2 py-1 text-right font-mono">{hrs(it.total_logged_in_minutes)}</td>
                            <td className="px-2 py-1 text-right font-mono">{hrs(it.total_active_minutes)}</td>
                            <td className="px-2 py-1 text-right font-mono">{hrs(it.total_idle_minutes)}</td>
                            <td className="px-2 py-1 text-right font-mono">{hrs(it.total_break_minutes)}</td>
                            <td className="px-2 py-1 text-right font-mono font-semibold">{it.activity_pct}%</td>
                            <td className="px-2 py-1">
                                <Badge className={CLASS_BADGE[it.classification] || ""}>
                                    {it.classification}
                                </Badge>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
